// src/EnhancedComponents/Login.js
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import ColorModeToggle from "./ColorModeToggle";
import { startSession } from "./auth";

const LOGO = "/assets/img/Logo_Bhopal_Jamaat.png";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(
    location.state?.expired ? "Your session has expired. Please sign in again." : ""
  );

  useEffect(() => {
    document.title = "Sign in · Bhopal Jamaat Admin";
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const Mobile = mobile.trim();
    if (!Mobile || !password) {
      setError("Enter your mobile number and password.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post("/AdminLogin.php", { Mobile, Password: password });
      if (data?.status === "Valid" && data.token) {
        startSession(data.token);
        navigate(from, { replace: true });
        return;
      }
      setError("Invalid mobile number or password.");
    } catch (err) {
      setError(
        err.response
          ? "The server could not process the request. Please try again later."
          : "Unable to reach the server. Check your internet connection."
      );
      console.error("Login error:", err);
    }
    setSubmitting(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.1fr 1fr" },
        bgcolor: "background.default",
      }}
    >
      {/* Brand panel (desktop) */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          gap: 3,
          p: 8,
          color: "#fff",
          background:
            "radial-gradient(circle at 20% 20%, rgba(30,136,229,0.45), transparent 55%), linear-gradient(160deg, #0b2545 0%, #13315c 60%, #134074 100%)",
        }}
      >
        <Box
          component="img"
          src={LOGO}
          alt=""
          sx={{ height: 88, bgcolor: "#fff", borderRadius: 3, p: 1.5 }}
        />
        <Typography variant="h4" component="p" sx={{ maxWidth: 440, lineHeight: 1.3 }}>
          Haidery Masjid Majlis Management
        </Typography>
        <Typography sx={{ opacity: 0.8, maxWidth: 420 }}>
          Manage area admins, events and live sessions for Bhopal Jamaat from one place.
        </Typography>
      </Box>

      {/* Form */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 2, sm: 4 },
        }}
      >
        <ColorModeToggle sx={{ position: "absolute", top: 16, right: 16 }} />

        <Paper
          variant="outlined"
          component="main"
          sx={{ width: "100%", maxWidth: 420, p: { xs: 3, sm: 4.5 }, borderRadius: 4 }}
        >
          <Stack alignItems="center" spacing={1} mb={3}>
            <Box
              component="img"
              src={LOGO}
              alt="Bhopal Jamaat"
              sx={{
                display: { md: "none" },
                height: 72,
                bgcolor: "#fff",
                borderRadius: 2,
                p: 1,
                mb: 1,
              }}
            />
            <Typography variant="h5" component="h1">
              Admin sign in
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Use your registered mobile number and password.
            </Typography>
          </Stack>

          <Box component="form" onSubmit={submit} noValidate>
            <Stack spacing={2.5}>
              {error && (
                <Alert severity="error" role="alert">
                  {error}
                </Alert>
              )}

              <TextField
                label="Mobile number"
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                autoComplete="username"
                autoFocus
                required
                fullWidth
                disabled={submitting}
                slotProps={{
                  htmlInput: { inputMode: "tel" },
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIphoneRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                fullWidth
                disabled={submitting}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((s) => !s)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <VisibilityOffRoundedIcon fontSize="small" />
                          ) : (
                            <VisibilityRoundedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{ py: 1.4 }}
              >
                {submitting ? "Signing in…" : "Sign in"}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
